import {
    COLOR_CATAPULT_BLACK,
    COLOR_CATAPULT_WHITE,
    COLOR_DKSFC_ALERT_RED,
    COLOR_DKSFC_INFORMATION_BLUE,
    COLOR_DKSFC_SUCCESS_GREEN,
    COLOR_DKSFC_WARNING_YELLOW,
    COLOR_LTSFC_ALERT_RED,
    COLOR_LTSFC_INFORMATION_BLUE,
    COLOR_LTSFC_SUCCESS_GREEN,
    COLOR_LTSFC_WARNING_YELLOW,
} from '@catapultsports/referee-design-tokens';
import { cx } from 'linaria';
import * as React from 'react';
import { IconAlert, IconCancel, IconCheckCircle, IconInfo, IconWarning } from '../foundation/icons/icons';
import { AlertContext } from './alert';
import * as styles from './alert.styles';
import { Button } from '../button';
export type AlertVariants = 'info' | 'alert' | 'success' | 'warning';

export interface AlertItemProps {
    open: boolean;
    title: string;
    dark?: boolean;
    variant?: AlertVariants;
    autoClose?: boolean;
    delay?: number;
    dismissible?: boolean;
    /**
     * Called after close animation has completed.
     */
    onClose?: (id?: string) => void;
    id?: string;
}

export const AlertItem = React.forwardRef<HTMLDivElement, React.PropsWithChildren<AlertItemProps>>((props, ref) => {
    const parentAlertProps = React.useContext(AlertContext);
    const {
        open,
        dark = parentAlertProps.dark,
        title,
        variant,
        children,
        onClose,
        autoClose,
        delay: autoCloseDelay = 5000,
        dismissible,
        id,
    } = props;

    const Icon = React.useCallback(() => {
        switch (variant) {
            case 'alert':
                return (
                    <IconAlert color={dark ? COLOR_DKSFC_ALERT_RED : COLOR_LTSFC_ALERT_RED} height="16" width="16" />
                );
            case 'success':
                return (
                    <IconCheckCircle
                        color={dark ? COLOR_DKSFC_SUCCESS_GREEN : COLOR_LTSFC_SUCCESS_GREEN}
                        height="16"
                        width="16"
                    />
                );
            case 'warning':
                return (
                    <IconWarning
                        color={dark ? COLOR_DKSFC_WARNING_YELLOW : COLOR_LTSFC_WARNING_YELLOW}
                        height="16"
                        width="16"
                    />
                );
            default:
                return (
                    <IconInfo
                        color={dark ? COLOR_DKSFC_INFORMATION_BLUE : COLOR_LTSFC_INFORMATION_BLUE}
                        height="16"
                        width="16"
                    />
                );
        }
    }, [dark, variant]);

    const InlineVariantStyle = React.useCallback(() => {
        switch (variant) {
            case 'alert':
                return dark ? styles.inlineDarkErrorStyle : styles.inlineErrorStyle;
            case 'success':
                return dark ? styles.inlineDarkSuccessStyle : styles.inlineSuccessStyle;
            case 'warning':
                return dark ? styles.inlineDarkWarningStyle : styles.inlineWarningStyle;
            default:
                return dark ? styles.inlineDarkAlert : styles.inlineAlert;
        }
    }, [dark, variant]);

    const handleClose = React.useCallback(() => {
        if (onClose) {
            const timeout = setTimeout(() => onClose(id), 200);
            return () => clearTimeout(timeout);
        }
    }, [id, onClose]);

    React.useEffect(() => {
        if (open && autoClose) {
            const timeout = setTimeout(handleClose, autoCloseDelay);
            return () => clearTimeout(timeout);
        }
    }, [autoClose, autoCloseDelay, handleClose, open]);

    return (
        <div
            ref={ref}
            className={cx(
                styles.alertItem,
                open && styles.open,
                parentAlertProps.inline && styles.inlineBase,
                parentAlertProps.inline && InlineVariantStyle(),
                dark && styles.darkModeAlert,
            )}
        >
            <div className={cx(styles.alertCancelContent)}>
                <div className={cx(styles.alertContent)}>
                    <Icon />
                    <div className={cx(styles.alertMessage)}>
                        <h1 className={cx(styles.alertTitle)}>{title}</h1>
                        {children}
                    </div>
                </div>
                {!autoClose || dismissible ? (
                    <Button
                        onClick={handleClose}
                        variant={'text'}
                        size={'small'}
                        className={cx(styles.alertDismissButton)}
                        themeOverride={dark ? 'dark' : 'light'}
                    >
                        <IconCancel
                            height={16}
                            width={16}
                            color={dark ? COLOR_CATAPULT_WHITE : COLOR_CATAPULT_BLACK}
                            data-testid="close-button"
                        />
                    </Button>
                ) : null}
            </div>
        </div>
    );
});

AlertItem.displayName = 'AlertItem';
